import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, Calendar, Rocket, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BetaCountdown = ({ countdown, phase, betaStartDate, betaEndDate }) => {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!countdown) return;
    
    const targetDate = countdown.target === 'start' 
      ? new Date(betaStartDate)
      : new Date(betaEndDate);

    const updateCountdown = () => {
      const now = new Date();
      const diff = targetDate - now;
      
      if (diff <= 0) {
        setTimeLeft(null);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setTimeLeft({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [countdown, betaStartDate, betaEndDate]);

  if (!timeLeft) return null;

  const isBeforeStart = phase === 'registration';
  const title = isBeforeStart 
    ? t('beta.countdownToStart', 'Beta Starts In')
    : t('beta.countdownToEnd', 'Beta Ends In');
  
  const Icon = isBeforeStart ? Rocket : Clock;
  const accentColor = isBeforeStart ? '#D4AF37' : '#E5C585';

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-8"
      data-testid="beta-countdown"
    >
      <div className={`relative overflow-hidden border ${isBeforeStart ? 'border-[#D4AF37]/50 bg-gradient-to-r from-[#D4AF37]/10 via-[#0A0A0A] to-[#D4AF37]/10' : 'border-[#E5C585]/30 bg-gradient-to-r from-[#E5C585]/5 via-[#0A0A0A] to-[#E5C585]/5'} p-6`}>
        {/* Animated background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(212,175,55,0.1)_50%,transparent_75%)] bg-[length:250%_250%] animate-shimmer" />
        </div>
        
        <div className="relative z-10">
          {/* Header */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <Icon className="text-[#D4AF37]" size={24} />
            <h3 className="font-serif text-xl text-white">{title}</h3>
          </div>

          {/* Countdown Grid */}
          <div className="grid grid-cols-4 gap-4 max-w-md mx-auto">
            {[
              { value: timeLeft.days, label: t('beta.days', 'Days') },
              { value: timeLeft.hours, label: t('beta.hours', 'Hours') },
              { value: timeLeft.minutes, label: t('beta.minutes', 'Min') },
              { value: timeLeft.seconds, label: t('beta.seconds', 'Sec') }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="bg-[#0A0A0A] border border-[#27272A] p-3">
                  <motion.span 
                    key={item.value}
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    className="block text-3xl md:text-4xl font-serif text-white"
                  >
                    {String(item.value).padStart(2, '0')}
                  </motion.span>
                </div>
                <span className="text-[#71717A] text-xs mt-2 block font-accent tracking-wider">
                  {item.label}
                </span>
              </div>
            ))}
          </div>

          {/* Beta Dates Info */}
          <div className="mt-6 flex items-center justify-center gap-6 text-sm text-[#71717A]">
            <div className="flex items-center gap-2">
              <Calendar size={14} className="text-[#D4AF37]" />
              <span>{t('beta.startDate', 'Start')}: April 1, 2026</span>
            </div>
            <div className="w-px h-4 bg-[#27272A]" />
            <div className="flex items-center gap-2">
              <CheckCircle size={14} className="text-[#D4AF37]" />
              <span>{t('beta.endDate', 'End')}: April 30, 2026</span>
            </div>
          </div>

          {/* Phase-specific message */}
          {isBeforeStart && (
            <p className="text-center text-[#A1A1AA] text-sm mt-4">
              {t('beta.registrationOpen', 'Registration is open! Secure your spot now.')}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default BetaCountdown;
